import React, { useState, useRef } from "react";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onFileUpload?: (file: File) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onFileUpload,
  disabled = false,
}) => {
  const [message, setMessage] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if there's a message OR a file, but not both empty
    if ((message.trim() || attachedFile) && !disabled) {
      // Send message if there's text
      if (message.trim()) {
        onSendMessage(message.trim());
      }

      // Send file if there's an attached file
      if (attachedFile && onFileUpload) {
        onFileUpload(attachedFile);
      }

      setMessage("");
      setAttachedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("📁 ChatInput - File selected:", e.target.files);
    const file = e.target.files?.[0];
    if (file) {
      console.log(
        "📁 ChatInput - Setting attached file:",
        file.name,
        file.size
      );
      setAttachedFile(file);
      // Reset l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openFileSelector = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("📁 ChatInput - Opening file selector");
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeAttachedFile = () => {
    console.log("📁 ChatInput - Removing attached file");
    setAttachedFile(null);
  };

  // Drag and Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    setDragCounter((prev) => prev + 1);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragCounter((prev) => prev - 1);
    if (dragCounter <= 1) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragCounter(0);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      console.log("📁 ChatInput - File dropped:", file.name, file.size);
      setAttachedFile(file);
    }
  };

  // Debug: Log props
  console.log("📁 ChatInput - Props:", {
    onFileUpload: !!onFileUpload,
    disabled,
  });

  return (
    <div className="relative">
      <div
        className={`relative w-full p-2 rounded-3xl border transition-all duration-200 ${
          isDragOver ? "border-synkColor bg-synkColor/10" : "border-gray-800"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col justify-start gap-2 relative w-full">
          <div className="flex justify-start gap-2 w-full overflow-visible relative">
            <div className="w-full relative flex items-center gap-3 min-h-[72px] py-2 px-3 bg-zinc-800 rounded-2xl overflow-hidden shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),_0px_1px_0px_0px_rgba(25,28,33,0.02),_0px_0px_0px_1px_rgba(25,28,33,0.08)] transition duration-1000">
              {/* File Upload Button - Simplified and more visible */}
              <button
                type="button"
                onClick={openFileSelector}
                className="size-8 rounded-xl transition-colors bg-white hover:bg-white/80 flex items-center justify-center flex-shrink-0 cursor-pointer z-50 relative"
                disabled={disabled}
                title="Attach file"
                style={{ zIndex: 1000 }}
              >
                <svg
                  className="inline-flex size-4 text-black"
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10.005 2.38a.75.75 0 0 1 .75.75v6.125h6.125a.75.75 0 0 1 .743.648l.007.102a.75.75 0 0 1-.75.75h-6.125v6.125a.75.75 0 0 1-.648.743l-.102.007a.75.75 0 0 1-.75-.75v-6.125H3.13a.75.75 0 0 1-.743-.648l-.007-.102a.75.75 0 0 1 .75-.75h6.125V3.13a.75.75 0 0 1 .648-.743l.102-.007z"></path>
                </svg>
              </button>

              <textarea
                ref={textareaRef}
                placeholder="Talk to Nova..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 relative text-sm whitespace-pre-line resize-none z-50 border-none text-white bg-transparent rounded-xl focus:outline-none focus:ring-0 overflow-y-auto scrollbar-none"
                disabled={disabled}
                style={{
                  height: "65px",
                  minHeight: "65px",
                  paddingTop: "22px",
                  paddingBottom: "23px",
                  lineHeight: "20px",
                }}
              />

              <button
                type="submit"
                onClick={handleSubmit}
                className="size-10 rounded-xl text-heading-str bg-gradient-to-b from-[#FFFFFF] to-[#E2E2E2] shadow-[0_3px_4px_-1px_rgba(0,0,0,0.15),0px_1px_0px_0px_rgba(255,255,255,0.33)_inset,0px_0px_0px_1px_#D4D4D4] after:absolute after:inset-0 after:rounded-xl after:bg-white/30 after:opacity-0 after:transition-opacity hover:after:opacity-100 flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={(!message.trim() && !attachedFile) || disabled}
              >
                <svg
                  className="inline-flex size-5 -rotate-90 text-black"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                >
                  <path d="M12.183 4.3a.75.75 0 0 1 1.061 0l3.44 3.441a3.25 3.25 0 0 1 0 4.596l-3.44 3.441a.75.75 0 0 1-1.061-1.061l3.441-3.441a1.75 1.75 0 0 0 .298-.397l.043-.091H3.13a.75.75 0 0 1-.743-.648l-.007-.102a.75.75 0 0 1 .75-.75h12.836l-.044-.09c-.052-.095-.114-.186-.184-.272l-.114-.125-3.44-3.441a.75.75 0 0 1 0-1.061z"></path>
                </svg>
              </button>
            </div>
          </div>

          {/* Show attached file info */}
          {attachedFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-xl border border-gray-600">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              <span className="text-sm text-gray-300 flex-1 truncate">
                {attachedFile.name}
              </span>
              <button
                type="button"
                onClick={removeAttachedFile}
                className="text-gray-400 hover:text-red-400 transition-colors"
                title="Remove file"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="*/*"
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
